import { Injectable } from "@angular/core";
import { Patient } from "@stockcare-storage/patient/patient";
import { InternalDatabaseService } from "../internal/internal-database.service";
import { IPatient } from "@stockcare-models/IPatient";

@Injectable({
  providedIn: "root",
})
export class PatientStorageService {
  readonly table: string;
  readonly CaseNumberIndex: string;

  constructor(private internalDatabaseService: InternalDatabaseService) {
    const table = Patient.getTableDefinition();
    this.table = table.TableName;
    this.CaseNumberIndex = table.Indexes[0].Name;
  }

  async addPatients(patients: Patient[]) {
    await this.internalDatabaseService.addRange(this.table, patients);
  }

  async addPatient(patient: Patient) {
    await this.internalDatabaseService.add(this.table, patient);
  }

  async getAllPatients(): Promise<Patient[]> {
    return await this.internalDatabaseService.getAll<Patient>(this.table);
  }

  async getAllKeys(): Promise<string[]> {
    return await this.internalDatabaseService.getAllKeys(this.table);
  }

  async getPatientById(id: number): Promise<Patient> {
    return await this.internalDatabaseService.getByPrimaryKey<Patient>(
      this.table,
      id
    );
  }

  async getPatientByIndex(caseNumber: string): Promise<Patient> {
    return await this.internalDatabaseService.getByIndex<Patient>(
      this.table,
      this.CaseNumberIndex,
      caseNumber
    );
  }

  async delete(primaryKey: string) {
    return await this.internalDatabaseService.delete(this.table, primaryKey);
  }

  async deleteAll() {
    await this.internalDatabaseService.deleteAll(this.table);
  }

  async updatePatient(patient: Patient): Promise<void> {
    await this.internalDatabaseService.update<Patient>(this.table, patient);
  }

  map(patients: IPatient[]) {
    const dbPatients: Patient[] = [];

    for (const patient of patients) {
      const dbPatient = new Patient();
      dbPatient.Id = patient.id;
      dbPatient.ReferenceId = patient.referenceId;
      dbPatient.CreatedTime = patient.createdTime;
      dbPatient.CreatedByUserId = patient.createdByUserId;
      dbPatient.UpdatedTime = patient.updatedTime;
      dbPatient.UpdatedByUserId = patient.updatedByUserId;
      dbPatient.Name = patient.name;
      dbPatient.PhysicalLocationId = patient.physicalLocationId;
      dbPatient.Bed = patient.bed;
      dbPatient.Room = patient.room;
      dbPatient.CaseNumber = patient.caseNumber;
      dbPatient.Discharged = patient.discharged;
      dbPatient.AdmittedDate = patient.admittedDate;
      dbPatient.DischargedDate = patient.dischargedDate;
      dbPatient.PatientStatus = patient.patientStatus;
      dbPatient.AttendingPhysicianId = patient.attendingPhysician?.id;
      dbPatient.AttendingPhysicianTitle = patient.attendingPhysician?.title;
      dbPatient.AttendingPhysicianName = patient.attendingPhysician?.name;
      dbPatient.AttendingPhysicianTelephoneNumber =
        patient.attendingPhysician?.telephoneNumber;
      dbPatient.Department = patient.department;
      dbPatient.NursingOrganizationUnit = patient.nursingOrganizationUnit;
      dbPatient.MedicalAidDescription = patient.medicalAidDescription;
      dbPatient.CanBeMovedToICU = patient.canBeMovedToIcu;
      dbPatient.LastScored = patient.lastScored;
      dbPatient.LastScore = patient.lastScore;
      dbPatients.push(dbPatient);
    }

    return dbPatients;
  }
}
